import { useCallback, useMemo, useState } from 'react';
import type { GetMmProgramResponse } from '@/services/api/mm-programs';
import { toDraft } from '../mm-program-editing';
import type { MarketMakerInput } from '../mm-program-schema';
import { type GridDocument, hasEdits } from './grid-rows';

interface StoredDraft {
  /** The loaded version the edits started from; a draft for an older version is dropped */
  version: number;
  document: MarketMakerInput;
}

export type LoadedDocument = GetMmProgramResponse & { pid: string };

/** The document the grid edits: the unsaved draft, or the loaded one without the retired fields drafts drop. */
export const editableOf = (document: GridDocument) => document.draft ?? toDraft(document.saved);

/**
 * Unsaved edits across market makers, shared by the grid and the market overview. Edits are kept per loaded version,
 * so once a save or reload brings a new version in, its old draft no longer applies.
 */
export const useProgramDrafts = (loaded: LoadedDocument[]) => {
  const [drafts, setDrafts] = useState<Record<string, StoredDraft>>({});

  const documents = useMemo(
    (): GridDocument[] =>
      loaded.map(({ pid, marketMaker, version, canEdit }) => {
        const draft = drafts[pid];
        return { pid, version, canEdit, saved: marketMaker, draft: draft?.version === version ? draft.document : undefined };
      }),
    [loaded, drafts],
  );

  const setDocuments = useCallback(
    (changed: Map<string, MarketMakerInput>) =>
      setDrafts((previous) => {
        const next = { ...previous };
        for (const { pid, version } of loaded) {
          const document = changed.get(pid);
          if (document) next[pid] = { version, document };
        }
        return next;
      }),
    [loaded],
  );

  const discard = useCallback(
    (pid?: string) =>
      setDrafts((previous) => {
        if (pid === undefined) return {};
        const { [pid]: _, ...rest } = previous;
        return rest;
      }),
    [],
  );

  const edited = useMemo(() => documents.filter(hasEdits), [documents]);

  return { documents, edited, setDocuments, discard };
};

export type ProgramDrafts = ReturnType<typeof useProgramDrafts>;
